import { CalendarDays, ExternalLink, Users, X } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { usePanel } from '@/app/panel'
import { Markdown } from '@/components/Markdown'
import { Button } from '@/components/ui/button'
import { Badge, EmptyState, Panel } from '@/components/ui/display'
import { useQuery } from '@tanstack/react-query'
import { findMeeting, getMeetingDetail } from '@/data/meetings'
import { findSpace } from '@/data/spaces'
import { clock } from '@/features/chat/Citation'
import { cn } from '@/lib/utils'

/**
 * A cited meeting, beside the answer that cited it.
 *
 * It shows the NOTES, not the transcript: the note is the edited version of the
 * same content and is what the answer was drawn from, so opening a citation
 * onto raw dialogue would ask the reader to do the extraction again themselves.
 * The claim is repeated at the top, because by the time the panel is open the
 * sentence that opened it may have scrolled away.
 */
export function MeetingPanel() {
  const { payload, close } = usePanel()
  const meeting = payload ? findMeeting(payload.meetingId) : undefined

  // The citation names a meeting; its notes are a second request, made here
  // rather than by whatever opened the panel, so a citation from anywhere in
  // the app opens onto the same thing.
  const detail = useQuery({
    queryKey: ['meeting', payload?.meetingId],
    queryFn: () => getMeetingDetail(payload!.meetingId),
    enabled: Boolean(payload?.meetingId),
  })
  const notes = detail.data?.notes ?? []
  const space = findSpace(meeting?.spaceId ?? null)

  return (
    <div className="flex min-h-0 flex-1 flex-col border-l border-[var(--color-border)] bg-[var(--color-surface)]">
      <div className="flex h-12 shrink-0 items-center gap-2 border-b border-[var(--color-border)] px-3">
        <CalendarDays className="size-3.5 shrink-0 text-[var(--color-fg-subtle)]" />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-xs font-semibold text-[var(--color-fg)]">
            {meeting?.title ?? 'Meeting'}
          </span>
          {meeting ? (
            <span className="block truncate text-2xs text-[var(--color-fg-subtle)]">
              {new Date(meeting.startsAt).toLocaleString(undefined, {
                dateStyle: 'medium',
                timeStyle: 'short',
              })}
            </span>
          ) : null}
        </span>
        {space ? <Badge tone="outline">{space.code}</Badge> : null}
        <Button size="iconSm" variant="ghost" onClick={close} aria-label="Close panel">
          <X className="size-3.5" />
        </Button>
      </div>

      {!meeting ? (
        <EmptyState title="That meeting is no longer in the list." />
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto p-3">
          {payload?.claim ? (
            <p className="mb-3 border-l-2 border-[var(--color-citation)] bg-[var(--color-citation-bg)] px-2.5 py-2 text-2xs leading-relaxed text-[var(--color-fg-muted)]">
              {payload.claim}
            </p>
          ) : null}

          {/* The words the claim rests on, above the notes rather than inside
              them: the reader came here to check a sentence, and the notes are
              a different question that happens to share a meeting. */}
          {payload?.turns?.length ? (
            <div className="mb-3 space-y-1.5 rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] px-2.5 py-2">
              {payload.turns.map((turn) => (
                <div
                  key={turn.turnId}
                  className={cn('text-2xs leading-relaxed', turn.context && 'opacity-45')}
                >
                  <span className="font-medium text-[var(--color-fg)]">{turn.speaker}</span>
                  {turn.startSeconds !== null ? (
                    <span className="tnum ml-1.5 text-[var(--color-fg-subtle)]">
                      {clock(turn.startSeconds)}
                    </span>
                  ) : null}
                  <p className="mt-0.5 text-[var(--color-fg-muted)]">{turn.text}</p>
                </div>
              ))}
            </div>
          ) : null}

          <p className="mb-3 flex items-center gap-1.5 text-2xs text-[var(--color-fg-subtle)]">
            <Users className="size-3" />
            {meeting.participants.map((person) => person.name).join(', ') || 'No participants recorded'}
          </p>

          {notes.length === 0 ? (
            <EmptyState title="No notes on this meeting." className="py-8" />
          ) : (
            <div className="space-y-2">
              {notes.map((note) => (
                <Panel key={note.id}>
                  <div className="flex items-center gap-2 border-b border-[var(--color-border-subtle)] px-3 py-1.5">
                    <h4 className="min-w-0 flex-1 truncate text-xs font-medium text-[var(--color-fg)]">
                      {note.title}
                    </h4>
                    {note.sharedExternally ? <Badge tone="outline">External</Badge> : null}
                  </div>
                  <div className="px-3 py-2.5">
                    {note.body ? (
                      <Markdown className="prose-chat text-xs text-[var(--color-fg)]">
                        {note.body}
                      </Markdown>
                    ) : (
                      <p className="text-2xs text-[var(--color-fg-subtle)]">Nothing written yet.</p>
                    )}
                  </div>
                </Panel>
              ))}
            </div>
          )}

          <Link
            to="/meetings"
            onClick={close}
            className="mt-3 inline-flex items-center gap-1.5 text-2xs text-[var(--color-fg-muted)] hover:text-[var(--color-fg)]"
          >
            <ExternalLink className="size-3" />
            Open in Meetings
          </Link>
        </div>
      )}
    </div>
  )
}
